import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Typography, Switch, Tooltip, IconButton, Fab, Box, Button, Modal,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useAuth } from '../context/AuthContext';
import { useAlerts, useUpdateAlert } from '../hooks/useAlerts';
import { useNotification } from '../context/NotificationContext';

const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('hr-HR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

const sortRows = (rows, key, dir) =>
  [...rows].sort((a, b) => {
    let av, bv;
    switch (key) {
      case 'name': av = a.name ?? ''; bv = b.name ?? ''; break;
      case 'terms': av = (a.terms?.length ?? 0); bv = (b.terms?.length ?? 0); break;
      case 'notification_mode': av = a.notification_mode ?? ''; bv = b.notification_mode ?? ''; break;
      case 'last_turned_on': av = a.last_turned_on ?? ''; bv = b.last_turned_on ?? ''; break;
      case 'is_active': av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
      default: av = ''; bv = '';
    }
    if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });

const toggleDir = (prev, key) => ({
  key,
  dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
});

const AlertScreen = () => {
  const { user } = useAuth();
  const { data: alerts = [] } = useAlerts();
  const navigate = useNavigate();
  const updateAlert = useUpdateAlert();
  const { showSuccess, showError } = useNotification();

  const alertLimit = user?.alert_limit ?? 0;

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  useEffect(() => { document.title = 'Alerti | Media Scanner'; }, []);

  const handleToggle = (alert) => {
    updateAlert.mutate(
      { ...alert, is_active: !alert.is_active },
      {
        onSuccess: () => showSuccess(alert.is_active ? 'Alert deaktiviran' : 'Alert aktiviran'),
        onError: () => showError('Greška pri promjeni statusa alerta'),
      }
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    updateAlert.mutate(
      { ...deleteTarget, is_archived: true },
      {
        onSuccess: () => {
          setConfirmDeleteOpen(false);
          setDeleteTarget(null);
          showSuccess('Alert obrisan');
        },
        onError: () => showError('Greška pri brisanju alerta'),
      }
    );
  };

  const handleEdit = (alert) => {
    navigate('/alarmDetails', { state: { alarm: alert } });
  };

  const sortProps = (key) => ({
    active: sort.key === key,
    direction: sort.key === key ? sort.dir : 'asc',
    onClick: () => setSort(prev => toggleDir(prev, key)),
  });

  const sortedAlerts = sortRows(alerts, sort.key, sort.dir);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            Alerti
          </Typography>
          <Chip
            label={`${alerts.length} / ${alertLimit}`}
            size="small"
            sx={{ fontWeight: 600, backgroundColor: '#e3f2fd', color: '#0079bf' }}
          />
        </Box>

        {/* Table */}
        {alerts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsOffIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Nema postavljenih alerta.
            </Typography>
            {alerts.length < alertLimit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => navigate('/alertCreate')}
              >
                Kreiraj prvi alert
              </Button>
            )}
          </Paper>
        ) : (
          <Paper sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('name')}>Naziv</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('terms')}>Pojmovi</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('notification_mode')}>Obavijest</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('last_turned_on')}>Zadnji pronalazak</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('is_active')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAlerts.map((alert) => (
                  <TableRow key={alert.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#0079bf',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => handleEdit(alert)}
                      >
                        {alert.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#555', maxWidth: 250 }}>
                      {Array.isArray(alert.terms) && alert.terms.length
                        ? alert.terms.join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                          backgroundColor: alert.notification_mode === 'immediate' ? '#e3f2fd' : '#fff3e0',
                          color: alert.notification_mode === 'immediate' ? '#1565c0' : '#e65100',
                        }}
                      >
                        {alert.notification_mode === 'daily_digest' ? 'Sažetak' : 'Odmah'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {formatDate(alert.last_turned_on)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                            backgroundColor: alert.is_active ? '#e8f5e9' : '#f5f5f5',
                            color: alert.is_active ? '#27ae60' : '#999',
                          }}
                        >
                          {alert.is_active ? 'Aktivan' : 'Neaktivan'}
                        </Typography>
                        <Tooltip title={alert.is_active ? "Deaktiviraj" : "Aktiviraj"}>
                          <Switch
                            size="small"
                            checked={alert.is_active}
                            onChange={() => handleToggle(alert)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Uredi">
                        <IconButton size="small" onClick={() => handleEdit(alert)}>
                          <EditNoteIcon sx={{ color: '#0079bf', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Obriši">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteTarget(alert);
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* FAB */}
      {alerts.length < alertLimit && (
        <Fab
          color="primary"
          aria-label="Dodaj alert"
          sx={{ position: 'fixed', bottom: 32, right: 32, boxShadow: 3, zIndex: 1000 }}
          onClick={() => navigate('/alertCreate')}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Delete confirmation modal */}
      <Modal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="confirm-delete-alert-title"
      >
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography id="confirm-delete-alert-title" variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Brisanje alerta
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Jeste li sigurni da želite obrisati alert "{deleteTarget?.name}"? Ova akcija se ne može poništiti.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setConfirmDeleteOpen(false);
                setDeleteTarget(null);
              }}
            >
              Odustani
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Izbriši
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default AlertScreen;
